import { CommonModule, Location } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { userRoles } from '../../../../environments/environment';
import { ButtonComponent } from '../../../shared/components/custom-button/button.component';
import { TableComponent } from '../../../shared/components/custom-table/table.component';
import { AddEventMCCPartnersComponent } from '../../../shared/components/dialog/add-mcc-partners/add-mcc-partner.component';
import { AlertDialogComponent, AlertDialogData } from '../../../shared/components/dialog/alert-dialog/alert-dialog.component';
import { CommonDialogComponent } from '../../../shared/components/dialog/commom/commom-dialog.component';
import { CustomToastComponent } from '../../../shared/components/toast/custom-toast.component';
import { HttpService } from '../../../shared/services/http.service';

interface EventType {
    mccEventPartnerId: string;
    id: string;
    partnerName: string;
    mccEventPartnerName: string;
    contactName: string;
    contactEmail: string;
    contactPhone: string;
    status: string;
    engagementCount: number;
}

interface APIResponse {
    status: boolean;
    message: string;
    data: EventType | [];
}

@Component({
    selector: 'app-mcc-partners',
    standalone: true,
    imports: [
        CommonModule,
        TableComponent,
        ButtonComponent,
        MatDialogModule,
        AddEventMCCPartnersComponent,
        FormsModule
    ],
    templateUrl: './mcc-partners.component.html',
    styleUrls: ['./mcc-partners.component.scss'],
})
export class EventMCCPartnersComponent implements OnInit {
    @ViewChild('actionButtons') actionButtonsTemplate!: TemplateRef<any>;
    @ViewChild('addEventTypeContent') addEventTypeContent!: TemplateRef<any>;
    @ViewChild(TableComponent) tableComponent!: TableComponent;

    submissionLoading = false;
    loading = false;
    data: any[] = [];
    editingEventType: EventType | null = null;
    columns: string[] = ['S.No', 'partner name', 'contact person', 'Email', 'Phone', 'engagement count', 'status'];
    fullData: any[] = [];
    search: string = '';

    userRoles = {
        addNew: false,
        genarateReport: false,
        listItem: {
            edit: false,
            statusChange: false
        }
    }

    constructor(
        private dialog: MatDialog,
        private httpService: HttpService,
        private snackBar: MatSnackBar,
        private cdr: ChangeDetectorRef,
        private location: Location,
        private router: Router,
    ) { }

    ngOnInit(): void {
        this.fetchMCCPartners();

        const user = localStorage.getItem('userRole');
        if (user && user in userRoles) {
            this.userRoles = userRoles[user as keyof typeof userRoles]?.mccEventPartner
        }
    }

    fetchMCCPartners(): void {
        this.loading = true;

        this.httpService.get<APIResponse>('mcceventpartners?status=2').subscribe({
            next: (response) => {
                const dataArray = Array.isArray(response.data) ? response.data : response.data ? [response.data] : [];
                const previousSearchTerm = this.search;
                this.fullData = dataArray.map((item, idx) => ({
                    id: item.mccEventPartnerId,
                    'S.No': idx + 1,
                    'partner name': item.mccEventPartnerName || '',
                    'contact person': item.contactName || '',
                    'engagement count': item.engagementCount || '0',
                    'Email': item.contactEmail || '',
                    'Phone': item.contactPhone || '',
                    status: item.status || 'Active',
                    mccEventPartnerId: item.mccEventPartnerId,
                    mccEventPartnerName: item.mccEventPartnerName || '',
                    contactName: item.contactName || '',
                    isStatusClickable: true,
                }));
                if (previousSearchTerm && previousSearchTerm.trim() !== '') {
                    this.search = previousSearchTerm;
                    this.applyFilter();
                } else {
                    this.data = [...this.fullData];
                }
                this.cdr.detectChanges();
            },
            error: (err) => {
                this.showErrorToast(err?.message);
                this.loading = false;
            },
            complete: () => {
                this.loading = false;
            }
        });
    }

    private getStatusBooleanValue(status: string): boolean {
        return status === 'Active';
    }

    navigateReports(): void {
        this.router.navigate(['/reports/report-dashboard'], {
            state: { activeTab: 'Industry partner' }
        });
    }


    updateSearch(searchValue: string): void {
        this.search = searchValue;
        this.applyFilter();
    }

    ngAfterViewInit(): void {
        this.cdr.detectChanges();
    }

    applyFilter(): void {
        if (!this.search || this.search.trim() === '') {
            this.data = [...this.fullData];
            return;
        }

        // Match only the columns on screen. The rows also carry ids, GUIDs and internal flags,
        // and searching those makes rows with no visible match appear.
        const searchTerm = this.search.toLowerCase().trim();
        this.data = this.fullData.filter(item =>
            this.columns.some(column =>
                item[column]?.toString().toLowerCase().includes(searchTerm)
            )
        );

        this.resetPagination();
        this.cdr.detectChanges();
    }

    private resetPagination(): void {
        this.data = [...this.data];
        if (this.tableComponent) {
            this.tableComponent.currentPage = 1;
            this.tableComponent.cdr?.detectChanges();
        }
    }

    onEventTypeFormSubmit(formData: any): void {
        if (this.submissionLoading) return
        this.submissionLoading = true
        const statusValue = formData.status
        const dialogRefs = this.dialog.openDialogs;

        const currentDialogRef = dialogRefs[dialogRefs.length - 1];

        if (currentDialogRef && currentDialogRef.componentInstance instanceof CommonDialogComponent) {
            if (this.editingEventType) {
                currentDialogRef.componentInstance.data.confirmText = 'Updating...';
            } else {
                currentDialogRef.componentInstance.data.confirmText = 'Submitting...';
            }
            currentDialogRef.componentInstance.data.loading = true;
            currentDialogRef.componentInstance.cdr.detectChanges();
        }
        const payload = {
            mccEventPartnerName: formData.partnerName,
            contactEmail: formData.contactEmail,
            contactName: formData.contactName,
            contactPhone: formData.contactPhone,
            status: statusValue
        };

        const apiCall = this.editingEventType
            ? this.httpService.put<APIResponse>(`mcceventpartners/${this.editingEventType.mccEventPartnerId}`, payload)
            : this.httpService.post<APIResponse>('mcceventpartners', payload);

        apiCall.subscribe({
            next: (response) => {
                if (response.status) {
                    this.showSuccessToast(response.message);
                    this.dialog.closeAll();
                    this.fetchMCCPartners();
                    this.editingEventType = null;
                } else {
                    this.showErrorToast(response.message);
                    if (currentDialogRef && currentDialogRef.componentInstance instanceof CommonDialogComponent) {
                        currentDialogRef.componentInstance.data.confirmText = this.editingEventType ? 'Update' : 'Submit';
                        currentDialogRef.componentInstance.data.loading = false;
                        currentDialogRef.componentInstance.cdr.detectChanges();
                    }
                    this.submissionLoading = false;
                    return;
                }
                this.submissionLoading = false
            },
            error: (err) => {
                console.error('Failed to save MCC Partner:', err);
                this.showErrorToast(err?.message);
                if (currentDialogRef && currentDialogRef.componentInstance instanceof CommonDialogComponent) {
                    currentDialogRef.componentInstance.data.confirmText = this.editingEventType ? 'Update' : 'Submit';
                    currentDialogRef.componentInstance.data.loading = false;
                    currentDialogRef.componentInstance.cdr.detectChanges();
                }
                this.submissionLoading = false
            }
        });
    }

    openAddDialog(eventTypeToEdit: EventType | null = null): void {
        this.editingEventType = eventTypeToEdit;
        const initialData = eventTypeToEdit ? {
            ...eventTypeToEdit,
            status: this.getStatusBooleanValue(eventTypeToEdit.status)
        } : null;

        const dialogRef = this.dialog.open(CommonDialogComponent, {
            data: {
                title: eventTypeToEdit ? 'Edit partner' : 'Add new partner',
                confirmText: eventTypeToEdit ? 'Update' : 'Submit',
                cancelText: 'Cancel',
                template: this.addEventTypeContent,
                loading: false
            },
            width: '50.5rem',
            maxWidth: '100vw',
            disableClose: true,
            autoFocus: false
        });

        dialogRef.afterClosed().subscribe(() => {
            this.editingEventType = null;
        });
    }

    onEdit(eventType: EventType): void {
        this.openAddDialog(eventType);
    }

    onDelete(user: any): void {
        const dialogData: AlertDialogData = {
            title: 'Confirm delete',
            message: `Are you sure you want to delete ${user.partnerName}?`,
            okText: 'Delete',
            cancelText: 'Cancel',
            icon: 'warning',
            submessage: ""
        };

        const dialogRef = this.dialog.open(AlertDialogComponent, {
            width: '25.438rem',
            data: dialogData,
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.httpService.delete<APIResponse>(`mcceventpartners/${user.id}`).subscribe({
                    next: response => {
                        if (response?.status) {
                            this.fullData = this.fullData.filter(u => u.id !== user.id);
                            this.data = this.data.filter(u => u.id !== user.id);
                            this.adjustPaginationAfterDelete();
                            this.showSuccessToast(response.message);
                        } else {
                            this.showErrorToast(response.message);
                        }
                    },
                    error: (err) => {
                        this.showErrorToast(err?.message);
                    },
                });
            }
        });
    }

    onStatusClick(event: { row: any; id: any }): void {
        if (!this.userRoles.listItem.statusChange) return
        const { row, id } = event;
        const currentStatus = row.status;
        const newStatus = currentStatus === 'Active' ? 'Inactive' : 'Active';

        const dialogData: AlertDialogData = {
            title: 'Status update',
            message: `Are you sure you want to ${newStatus === 'Active' ? 'Activate' : 'Deactivate'} ?`,
            okText: 'Update',
            cancelText: 'Cancel',
            icon: 'warning',
            submessage: ""
        };

        const dialogRef = this.dialog.open(AlertDialogComponent, {
            width: '25.438rem',
            data: dialogData,
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                const apiStatus = newStatus === 'Active';
                this.httpService.put<APIResponse>(`mcceventpartners/${id}/status`, { status: apiStatus }).subscribe({
                    next: response => {
                        if (response?.status) {
                            this.updateLocalMCCPartnerStatus(id, newStatus);
                            this.showSuccessToast(response.message);
                        } else {
                            this.showErrorToast(response.message);
                        }
                    },
                    error: () => {
                        this.showErrorToast('An error occurred while updating the Industry Partner status.');
                    },
                });
            }
        });
    }

    private updateLocalMCCPartnerStatus(partnerId: string, newStatus: string): void {
        this.fullData = this.fullData.map(item => {
            if (item.id === partnerId) {
                return {
                    ...item,
                    status: newStatus
                };
            }
            return item;
        });

        this.data = this.data.map(item => {
            if (item.id === partnerId) {
                return {
                    ...item,
                    status: newStatus
                };
            }
            return item;
        });

        this.cdr.detectChanges();
    }

    goBack() {
        this.location.back();
    }

    private adjustPaginationAfterDelete(): void {
        if (!this.tableComponent) return;

        const totalPages = Math.ceil(this.data.length / this.tableComponent.pageSize);
        if (this.tableComponent.currentPage > totalPages && totalPages > 0) {
            this.tableComponent.currentPage = totalPages;
        }
    }

    private showSuccessToast(message: string): void {
        this.snackBar.openFromComponent(CustomToastComponent, {
            data: { message },
            duration: 3000,
            horizontalPosition: 'right',
            verticalPosition: 'top',
            panelClass: ['no-padding-snackbar', 'success-toast']
        });
    }

    private showErrorToast(message: string | null | undefined): void {
        if (!message) return;
        this.snackBar.openFromComponent(CustomToastComponent, {
            data: { message },
            duration: 3000,
            horizontalPosition: 'right',
            verticalPosition: 'top',
            panelClass: ['no-padding-snackbar', 'error-toast']
        });
    }
}